<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { HealthCenter, HealthCenterEdit, OpenMode } from "@/types";
import { diff, healthCenter } from "@/types";
import { mutateHealthCenterKey } from "@/logic/provide";
import { healthCentersPathName } from "@/router";
import { inject, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { MDBTextarea } from "mdb-vue-ui-kit";
import FormInput from "../FormInput.vue";

const props = defineProps<{
  modelValue: HealthCenter;
  openMode: OpenMode;
  minified?: boolean;
}>();
const router = useRouter();
const editMode = ref(false);
const minified = ref(props.minified ?? false);
const editCenter = inject(mutateHealthCenterKey);
const model = ref(healthCenter(props.modelValue)); // local copy

const emit = defineEmits<{
  "click:detail": [element: HealthCenter, offset: number];
  "click:remove": [element: HealthCenter];
}>();

function clickSelect(target: EventTarget | null) {
  if (!target) {
    return;
  }

  var me = target as HTMLElement;

  // Trigger a scroll into view when selected
  if (props.openMode === "Edit") {
    setTimeout(() => {
      me.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  while (!me.classList.contains("cntr-container") && me.offsetParent) {
    me = me.offsetParent as HTMLElement;
  }

  emit("click:detail", model.value, me.offsetTop);
}

function toggleEdit() {
  editMode.value = !editMode.value;
}

function cancel() {
  model.value = healthCenter(props.modelValue);
  editMode.value = false;
}

function save() {
  if (editCenter) {
    const patch: HealthCenterEdit = diff(props.modelValue, model.value);
    if (Object.keys(patch).length !== 0) {
      editCenter(props.modelValue.id, patch);
    }
  }
  editMode.value = false;
}

function onRemove() {
  emit("click:remove", model.value);
}

function onOpen() {
  router.push({
    name: healthCentersPathName,
    params: { id: props.modelValue.id },
  });
}

watch(props, () => {
  minified.value = props.minified ?? false;
});
</script>

<template>
  <div class="cntr-container">
    <div
      class="header-container"
      :class="{ pointer: openMode == 'Edit' }"
      @click.stop="clickSelect($event.target)"
    >
      <div class="d-header">
        <div class="spaceme">
          <img src="@/assets/icons/medical-center-bold.svg" class="icon" />
          <h2 class="l-name">{{ model.name }}</h2>
        </div>
        <h3>
          {{ model.contact_imported.zipcode }}
        </h3>
      </div>
    </div>
    <div class="content-container">
      <template v-if="minified">
        <!-- Combined -->
        <div class="two-cols-split">
          <!-- -->
          <div class="crow">
            <FormInput
              label="Adresse"
              type="text"
              v-model="model.contact_imported.address"
              :disabled="true"
            />
          </div>
          <div class="crow">
            <div style="width: 70px">
              <FormInput
                label=""
                type="text"
                v-model="model.contact_imported.zipcode"
                :disabled="true"
              />
            </div>
            <div style="width: 116px">
              <FormInput
                label="N. Finess"
                type="number"
                v-model="model.finess"
                :disabled="true"
              />
            </div>
            <div style="width: 100px">
              <FormInput
                label="Nb. dossiers"
                type="number"
                :model-value="model.cases.length"
                :disabled="true"
                v-if="!editMode"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <template v-if="editMode">
          <div class="crow">
            <!-- Name -->
            <FormInput
              label="Nom du centre"
              type="text"
              v-model="model.name"
              :disabled="true"
            />
          </div>
        </template>
        <div class="two-cols-split">
          <div class="crow">
            <!-- Editable ids -->
            <div style="flex-grow: 1">
              <FormInput
                label="Nom commercial"
                type="text"
                v-model="model.other_name"
                :disabled="!editMode"
              />
            </div>
            <div style="width: 116px">
              <FormInput
                label="N. Finess"
                type="number"
                v-model="model.finess"
                :disabled="true"
              />
            </div>
            <div style="width: 100px">
              <FormInput
                label="Nb. dossiers"
                type="number"
                :model-value="model.cases.length"
                :disabled="true"
                v-if="!editMode"
              />
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="two-cols-split">
          <!-- -->
          <div class="crow">
            <FormInput
              label="Adresse"
              type="text"
              v-model="model.contact_imported.address"
              :disabled="true"
            />
          </div>
          <div class="crow">
            <div style="width: 70px">
              <FormInput
                label=""
                type="text"
                v-model="model.contact_imported.zipcode"
                :disabled="true"
              />
            </div>
            <div>
              <FormInput
                label=""
                type="text"
                v-model="model.contact_imported.city"
                :disabled="true"
              />
            </div>
          </div>
        </div>

        <!-- Contact -->
        <div class="two-cols-split">
          <div class="crow">
            <FormInput
              label="Email"
              type="email"
              v-model="model.contact.email"
              :disabled="!editMode"
            />
          </div>
          <div class="crow">
            <div style="width: 130px">
              <FormInput
                label="Tel. Fixe"
                type="tel"
                v-model="model.contact_imported.phone_landline"
                :disabled="true"
              />
            </div>
            <div style="width: 130px">
              <FormInput
                label="Tel. Alternatif"
                type="tel"
                v-model="model.contact.phone_landline"
                :disabled="!editMode"
              />
            </div>
          </div>
        </div>

        <div class="crow">
          <MDBTextarea
            label="Notes"
            rows="3"
            v-model="model.notes"
            :disabled="!editMode"
          />
        </div>
      </template>
    </div>
    <div class="content-footer" v-if="!minified">
      <div v-if="openMode == 'Edit'" class="buttons">
        <!-- Edit -->
        <span v-if="editMode" class="button" @click.stop="cancel()">{{
          "Annuler"
        }}</span>
        <span v-if="editMode" class="button" @click.stop="save()">{{
          "Enregistrer"
        }}</span>
        <span v-else class="button" @click.stop="toggleEdit()">{{
          "Editer"
        }}</span>
      </div>
      <div v-else-if="openMode == 'InlineList'" class="buttons">
        <!-- View -->
        <span v-if="!editMode" class="button" @click.stop="onRemove()">{{
          "Retirer"
        }}</span>
        <span v-else class="button" @click.stop="cancel()">{{
          "Annuler"
        }}</span>
        <span v-if="editMode" class="button" @click.stop="save()">{{
          "Enregistrer"
        }}</span>
        <span v-else class="button" @click.stop="toggleEdit()">{{
          "Editer"
        }}</span>
        <span v-if="!editMode" class="button" @click.stop="onOpen()">{{
          "Voir les dossiers"
        }}</span>
      </div>
      <div v-else-if="openMode == 'Create'" class="buttons">
        <!-- Create -->
        <span class="button" @click.stop="cancel()">{{ "Annuler" }}</span>
        <span class="button" @click.stop="save()">{{ "Enregistrer" }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  display: inline-block;
  padding-right: 8px;
}

.entry {
  text-decoration: underline;
}

h2,
h3,
.colored {
  color: var(--vt-c-purple);
  &.no-color {
    color: inherit;
  }
}

h2.l-name,
h3.l-name {
  max-width: 500px;
  overflow: hidden;
  text-wrap: nowrap;
}

.nowrap {
  white-space: nowrap;
}

.pointer {
  cursor: pointer;
}

.cntr-container {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 680px;
  min-height: 40px;
  margin-right: auto;

  border-radius: 0 6px 6px 0;

  background-color: var(--color-background-soft);
  transition: all 0.1s ease-in-out;

  &.list:hover,
  &.selected {
    transition: transform 0.2s ease-in;
    transform: translateX(12px);
  }

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;

    border-width: var(--cdo-border-thin-width);
    border-bottom-style: solid;
    border-color: var(--vt-c-purple);

    white-space: nowrap;

    .d-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      width: 100%;
      margin-left: 1em;
      margin-right: 1em;
    }

    .spaceme {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;

      margin-right: 1em;
      flex-wrap: wrap;
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1.5em 1em 1em 1em;
    row-gap: 1.3em;

    .two-cols-split {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-evenly;
      column-gap: 1em;
    }

    .crow {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      flex-wrap: nowrap;
      column-gap: 1em;
      width: 100%;

      &.appart {
        justify-content: space-between;
      }

      &.v-space {
        margin-top: 1em;
      }
    }
  }

  .content-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    margin-left: 1em;
    margin-bottom: 1.25em;

    .buttons {
      span {
        margin-right: 1em;
      }
    }

    div:last-child {
      margin-right: 1em;
    }
  }
}
</style>
